/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { mono, monoShared } from '../monolith.tokens'

export interface MonolithOrderListRow {
	id: string
	date: string
	status: string
	/** 통화 기호까지 붙은 금액 */
	amount: string
}

interface PropsType {
	/** 스크린리더용 표 이름 */
	label: string
	rows: MonolithOrderListRow[]
	isLoading: boolean
	emptyLabel: string
	onDetail: (id: string) => void
	/** Download Excel — 목록이 비면 꺼진다 */
	onDownloadExcel: () => Promise<void>
	/** Download Excel 오른쪽의 페이지 고유 동작(예: Wholesale 의 New order) */
	actions?: React.ReactNode
	/** 머리와 표 사이(예: 주문 완료 안내) */
	notice?: React.ReactNode
}

/**
 * 계정 페이지 주문 목록 — My Order · Wholesale Orders 공용 (2026-10-06).
 * 머리 = Orders 키커 · Download Excel · (페이지 동작). 표 = Order No. · Date · Status · Amount · Detail.
 * 주문이 없어도 열 머리는 그린다(빈 목록을 문구 하나로 두지 않는다). 600px 아래에서는 한 주문이 세 줄로 접힌다.
 */
export const MonolithOrderList = (props: PropsType): JSX.Element => {
	const {
		label,
		rows,
		isLoading,
		emptyLabel,
		onDetail,
		onDownloadExcel,
		actions,
		notice,
	} = props
	const [excelState, setExcelState] = React.useState<'idle' | 'working' | 'error'>('idle')

	const onExcel = async () => {
		if (excelState === 'working' || rows.length === 0) return
		setExcelState('working')
		try {
			await onDownloadExcel()
			setExcelState('idle')
		} catch (err: unknown) {
			console.error('[order list] 엑셀 생성 실패', err)
			setExcelState('error')
		}
	}

	return (
		<View css={style} className="mnOrderListView">
			<View className="mnOrderListHead">
				<span className="mnKicker">Orders</span>
				<View className="mnOrderListActions">
					<button
						type="button"
						className="mnTextLink"
						disabled={rows.length === 0 || excelState === 'working'}
						onClick={() => void onExcel()}
					>
						{excelState === 'working'
							? 'Preparing'
							: excelState === 'error'
							? 'Download failed'
							: 'Download Excel'}
					</button>
					{actions}
				</View>
			</View>

			{notice}

			<View className="mnOrderTable2" role="table" aria-label={label}>
				<View className="mnOrderRow mnOrderRowHead" role="row">
					<span>Order No.</span>
					<span>Date</span>
					<span>Status</span>
					<span className="right">Amount</span>
					<span aria-hidden="true" />
				</View>
				{isLoading ? (
					<View className="mnOrderRow mnOrderRowQuiet" role="row">
						<span className="mnMeta">Loading</span>
					</View>
				) : rows.length === 0 ? (
					<View className="mnOrderRow mnOrderRowQuiet" role="row">
						<span>{emptyLabel}</span>
					</View>
				) : (
					rows.map(aRow => (
						<View className="mnOrderRow" role="row" key={aRow.id}>
							<span className="mnOrderRowNo">{aRow.id}</span>
							<span className="mnOrderRowDate">{aRow.date}</span>
							<span className="mnOrderRowStatus">{aRow.status}</span>
							<span className="right num mnOrderRowAmount">{aRow.amount}</span>
							<span className="right mnOrderRowAction">
								<button
									type="button"
									className="mnOrderDetailButton"
									onClick={() => onDetail(aRow.id)}
								>
									Detail
								</button>
							</span>
						</View>
					))
				)}
			</View>
		</View>
	)
}

const style = css({
	...monoShared,
	display: 'flex',
	flexDirection: 'column',
	gap: 20,
	width: '100%',

	'.mnOrderListHead': {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: 16,
		flexWrap: 'wrap',
	},
	'.mnOrderListActions': {
		display: 'flex',
		alignItems: 'center',
		gap: 28,
		'.mnTextLink:disabled': {
			color: mono.faint,
			borderBottomColor: 'transparent',
			cursor: 'default',
		},
	},

	'.mnOrderTable2': {
		width: '100%',
		borderTop: `1px solid ${mono.line}`,
	},
	'.mnOrderRow': {
		display: 'grid',
		gridTemplateColumns:
			'minmax(0, 1.6fr) minmax(0, 1fr) minmax(0, 1.2fr) minmax(0, 1fr) 76px',
		gap: 16,
		alignItems: 'center',
		padding: '16px 0',
		borderBottom: `1px solid ${mono.line}`,
		fontSize: 15,
		fontWeight: 300,
		color: mono.ink,
		'.right': { textAlign: 'right' },
		'.num': { fontVariantNumeric: 'tabular-nums' },
		'.mnOrderRowNo': { fontWeight: 400, overflowWrap: 'anywhere' },
		'.mnOrderRowDate': { color: mono.dim, fontVariantNumeric: 'tabular-nums' },
		'.mnOrderRowStatus': { color: mono.dim },

		// 자세히 — 작은 테두리 버튼(목록 행 안이라 CTA 높이 50 은 크다). 호버 = 채움
		'.mnOrderDetailButton': {
			height: 32,
			padding: '0 14px',
			border: `1px solid ${mono.ink}`,
			borderRadius: 2,
			background: 'transparent',
			fontSize: 13,
			fontWeight: 500,
			color: mono.ink,
			cursor: 'pointer',
			whiteSpace: 'nowrap',
			transition: 'background-color 150ms, color 150ms',
			'&:hover': { background: mono.ink, color: '#FFFFFF' },
			'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 2 },
		},

		// 폰 — 번호 · 금액 / 날짜 / 상태 + Detail
		'@media (max-width: 600px)': {
			gridTemplateColumns: 'minmax(0, 1fr) auto',
			rowGap: 6,
			'.mnOrderRowNo': { gridColumn: 1, gridRow: 1 },
			'.mnOrderRowAmount': { gridColumn: 2, gridRow: 1 },
			'.mnOrderRowDate': { gridColumn: 1, gridRow: 2, fontSize: 13 },
			'.mnOrderRowStatus': { gridColumn: 1, gridRow: 3, fontSize: 13 },
			'.mnOrderRowAction': { gridColumn: 2, gridRow: '2 / span 2', alignSelf: 'end' },
		},
	},
	'.mnOrderRowHead': {
		padding: '10px 0',
		fontSize: 12,
		letterSpacing: '.14em',
		textTransform: 'uppercase',
		fontWeight: 500,
		color: mono.faint,
		'@media (max-width: 600px)': { display: 'none' },
	},
	'.mnOrderRowQuiet': {
		display: 'block',
		padding: '28px 0',
		color: mono.dim,
	},
})
